import { __importDefault, __importStar } from "tslib";
import { Forwarder, FwPacket } from "@ndn/fw";
import { LpService } from "@ndn/lp";
import { Interest } from "@ndn/packet";
import { asDataView, pushable } from "@ndn/util";
import { abortableSource, AbortError as IteratorAbortError } from "abortable-iterator";
import _cjsDefaultImport0 from "retry"; const retry = __importDefault(_cjsDefaultImport0).default;
import { consume, filter, map, pipeline } from "streaming-iterables";
import { TypedEventTarget } from "typescript-event-target";
import { Transport } from "./transport_browser.js";
/** Network layer face for sending and receiving L3 packets. */
export class L3Face extends TypedEventTarget {
    transport;
    /**
     * Constructor.
     * @param transport - Initial transport. It may be replaced through reopen mechanism.
     * @param attributes - Additional attributes.
     * L3Face attributes consist of transport attributes overridden by these attributes.
     * @param lpOptions - NDNLPv2 service options.
     */
    constructor(transport, attributes = {}, lpOptions = {}) {
        super();
        this.transport = transport;
        this.attributes = {
            describe: `L3Face(${transport})`,
            advertiseFrom: false,
            ...transport.attributes,
            ...attributes,
        };
        this.lp = new LpService(lpOptions, transport);
        this.rx = this.makeRx();
    }
    /**
     * Attributes of a network layer face.
     *
     * @remarks
     * When L3Face is added to a logical forwarder, this is copied to {@link FwFace.attributes}.
     */
    attributes;
    lp;
    rx;
    wireTokenPrefix = Math.trunc(Math.random() * 0x10000);
    /**
     * Obtain face UP/DOWN state.
     * @remarks
     * Caller can get notifications about state transitions via state/up/down/close events.
     */
    get state() { return this.state_; }
    set state(newState) {
        if (newState === this.state_) {
            return;
        }
        const evt = new L3Face.StateEvent("state", newState, this.state_);
        this.state_ = newState;
        this.dispatchTypedEvent("state", evt);
        switch (newState) {
            case L3Face.State.UP: {
                this.dispatchTypedEvent("up", new Event("up"));
                break;
            }
            case L3Face.State.DOWN: {
                const err = this.lastError instanceof Error ?
                    this.lastError :
                    new Error(`${this.lastError ?? "unknown error"}`);
                this.dispatchTypedEvent("down", new CustomEvent("down", { detail: err }));
                this.lastError = undefined;
                break;
            }
            case L3Face.State.CLOSED: {
                this.dispatchTypedEvent("close", new Event("close"));
                break;
            }
        }
    }
    state_ = L3Face.State.UP;
    lastError;
    rxSources = pushable();
    reopenRetry;
    async *makeRx() {
        for await (const source of this.rxSources) {
            try {
                yield* this.rxTransform(source);
                this.lastError = new Error("RX ending");
                this.state = L3Face.State.DOWN;
            }
            catch (err) {
                if (!(err instanceof IteratorAbortError)) {
                    this.lastError = err;
                    this.state = L3Face.State.DOWN;
                }
            }
        }
    }
    async *rxTransform(transportRx) {
        yield* pipeline(() => transportRx, this.lp.rx, filter((pkt) => {
            if (pkt instanceof LpService.RxError) {
                this.dispatchTypedEvent("rxerror", new CustomEvent("rxerror", { detail: pkt }));
                return false;
            }
            return true;
        }), map(({ l3, token: wireToken, congestionMark }) => {
            let internalToken;
            if (l3 instanceof Interest) {
                internalToken = wireToken;
            }
            else if (wireToken?.length === 6) {
                const dv = asDataView(wireToken);
                if (dv.getUint16(0) === this.wireTokenPrefix) {
                    internalToken = dv.getUint32(2);
                }
            }
            return FwPacket.create(l3, internalToken, congestionMark);
        }));
    }
    txTransform(fwTx) {
        return pipeline(() => fwTx, filter((pkt) => FwPacket.isEncodable(pkt)), map(({ l3, token: internalToken, congestionMark }) => {
            let wireToken;
            if (typeof internalToken === "number") {
                wireToken = new Uint8Array(6);
                const dv = asDataView(wireToken);
                dv.setUint16(0, this.wireTokenPrefix);
                dv.setUint32(2, internalToken);
            }
            else if (internalToken instanceof Uint8Array) {
                wireToken = internalToken;
            }
            return { l3, token: wireToken, congestionMark };
        }), this.lp.tx, filter((value) => {
            if (value instanceof LpService.TxError) {
                this.dispatchTypedEvent("txerror", new CustomEvent("txerror", { detail: value }));
                return false;
            }
            return true;
        }));
    }
    tx = async (iterable) => {
        const txSourceIterator = this.txTransform(iterable)[Symbol.asyncIterator]();
        const txSourceIterable = {
            [Symbol.asyncIterator]: () => ({
                next: () => txSourceIterator.next(),
                async [Symbol.asyncDispose]() {
                    // https://github.com/zen-fs/core/issues/158
                },
            }),
        };
        while (this.state !== L3Face.State.CLOSED) {
            if (this.state === L3Face.State.DOWN) {
                this.reopenTransport();
            }
            const abort = new AbortController();
            this.addEventListener("state", () => abort.abort(), { once: true, signal: abort.signal });
            try {
                const txSource = abortableSource(txSourceIterable, abort.signal);
                if (this.state === L3Face.State.UP) {
                    this.rxSources.push(abortableSource(this.transport.rx, abort.signal));
                    await this.transport.tx(txSource);
                }
                else {
                    await consume(txSource);
                }
                this.state = L3Face.State.CLOSED;
            }
            catch (err) {
                if (!(err instanceof IteratorAbortError)) {
                    this.lastError = err;
                    this.state = L3Face.State.DOWN;
                }
            }
            finally {
                abort.abort();
            }
        }
        this.reopenRetry?.stop();
        this.rxSources.stop();
    };
    reopenTransport() {
        this.reopenRetry?.stop();
        this.reopenRetry = retry.operation({
            forever: true,
            minTimeout: 100,
            maxTimeout: 60000,
            randomize: true,
        });
        this.reopenRetry.attempt(async () => {
            try {
                this.transport = await this.transport.reopen();
            }
            catch (err) {
                if (!(err instanceof Transport.ReopenNotSupportedError)) {
                    this.reopenRetry.retry(err);
                }
                return;
            }
            if (this.state === L3Face.State.CLOSED) {
                // eslint-disable-next-line @typescript-eslint/no-empty-function
                void this.transport.tx((async function* () { })()); // shutdown transport
                return;
            }
            this.reopenRetry.stop();
            this.state = L3Face.State.UP;
        });
    }
}
(function (L3Face) {
    /** Face state. */
    let State;
    (function (State) {
        State[State["UP"] = 0] = "UP";
        State[State["DOWN"] = 1] = "DOWN";
        State[State["CLOSED"] = 2] = "CLOSED";
    })(State = L3Face.State || (L3Face.State = {}));
    class StateEvent extends Event {
        state;
        prev;
        constructor(type, state, prev) {
            super(type);
            this.state = state;
            this.prev = prev;
        }
    }
    L3Face.StateEvent = StateEvent;
    function makeCreateFace(createTransport) {
        return (async ({ fw = Forwarder.getDefault(), addRoutes, l3, lp, callback, }, ...args) => {
            const created = await createTransport(...args);
            const makeFace = (transport) => {
                const l3face = new L3Face(transport, { advertiseFrom: false, ...l3 }, lp);
                const fwFace = fw.addFace(l3face);
                processAddRoutes(fwFace, addRoutes);
                callback?.(transport, l3face, fwFace);
                return fwFace;
            };
            return Array.isArray(created) ? created.map(makeFace) : makeFace(created);
        });
    }
    L3Face.makeCreateFace = makeCreateFace;
    /**
     * Add routes to a FwFace.
     * @param fwFace - Target FwFace.
     * @param addRoutes - List of routes.
     * @remarks
     * This function is typically used for implementing {@link CreateFaceOptions.addRoutes}.
     */
    function processAddRoutes(fwFace, addRoutes = ["/"]) {
        for (const routeName of addRoutes) {
            fwFace.addRoute(routeName);
        }
    }
    L3Face.processAddRoutes = processAddRoutes;
})(L3Face || (L3Face = {}));
